<template>
  <div class="about-page">
    <v-container class="pb-8 pt-0">
      <v-row>
        <v-col cols="12">
          <div class="hero-shell">
            <v-carousel
              v-model="activeSlide"
              class="about-hero"
              height="420"
              cycle
              hide-delimiters
              :show-arrows="smAndDown ? false : 'hover'"
              :interval="6500"
            >
              <v-carousel-item v-for="slide in heroSlides" :key="slide.src">
                <v-img :src="slide.src" :alt="slide.alt" cover height="100%">
                  <div class="hero-scrim"></div>
                </v-img>
              </v-carousel-item>
            </v-carousel>

            <div v-if="smAndDown" class="hero-top-dots" aria-label="Carousel navigatie">
              <button
                v-for="(slide, index) in heroSlides"
                :key="slide.src"
                type="button"
                class="hero-dot"
                :class="{ 'hero-dot-active': index === activeSlide }"
                :aria-label="`Ga naar afbeelding ${index + 1}`"
                :aria-pressed="index === activeSlide"
                @click="setSlide(index)"
              ></button>
            </div>

            <div class="hero-overlay">
              <h1 class="hero-title text-h2 text-sm-h1 font-weight-bold mb-3">Over ons</h1>
              <p class="hero-copy text-body-1 mb-5">
                Wij zijn de Westerkerk in Eemdijk, een kleine maar hechte kerkgemeenschap waar
                mensen samenkomen rondom de Bijbel en het geloof in Jezus Christus.
              </p>
              <div class="hero-actions">
                <v-btn
                  class="btn-hero"
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-calendar-text"
                  to="/kerkdiensten"
                >
                  Bekijk preekrooster
                </v-btn>
                <v-btn
                  class="btn-hero"
                  color="primary"
                  variant="flat"
                  prepend-icon="mdi-email"
                  to="/contact"
                >
                  Neem contact op
                </v-btn>
              </div>
            </div>
          </div>

          <div class="mt-7">
            <p class="text-body-1">
              Wij zijn de Westerkerk in Eemdijk, een kleine maar hechte kerkgemeenschap met ongeveer
              200 leden. Elke zondag komen we samen rondom de Bijbel en ons geloof in Jezus
              Christus. We bidden en danken met elkaar en leven met elkaar mee. Ook vieren we samen
              het heilig avondmaal.
            </p>
            <p class="text-body-1">
              Naast de kerkdiensten zijn er bijbelstudies en miniwijken. Voor kinderen en jongeren
              is er kinder- en jeugdwerk, waaronder catechisaties. Daarnaast organiseert onze
              Activiteitencommissie regelmatig ontmoetingen en activiteiten.
            </p>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <section class="pt-5">
            <div class="mb-6">
              <p class="text-kicker text-highlight">Kennismaken?</p>
              <h2 class="text-h4 text-accent">Kom eens langs</h2>
              <p class="text-body-1 mb-0">
                Wil je een dienst bezoeken, meer weten over onze gemeente of even contact opnemen?
                Hieronder vind je de belangrijkste praktische informatie overzichtelijk bij elkaar.
              </p>
            </div>

            <v-row
              class="my-4 border-y border-opacity-25"
              style="border-color: rgba(80, 86, 143, 0.14) !important"
            >
              <v-col cols="12" sm="6" class="d-flex align-start ga-4">
                <v-avatar color="rgba(238, 179, 54, 0.12)" size="40">
                  <v-icon color="highlight" size="20">mdi-calendar-text</v-icon>
                </v-avatar>
                <div>
                  <h3 class="text-h6 text-accent">Diensten</h3>
                  <p class="text-body-1">
                    Elke zondag komen we samen in twee kerkdiensten. De ochtenddienst begint om
                    09.30 uur.
                  </p>
                  <p class="text-body-1">
                    {{ sundayEveningServiceDescription }}
                  </p>
                  <v-btn
                    color="highlight"
                    variant="text"
                    prepend-icon="mdi-arrow-right"
                    to="/kerkdiensten"
                    class="btn-link"
                  >
                    Bekijk dienstenrooster
                  </v-btn>
                </div>
              </v-col>

              <v-col cols="12" sm="6" class="d-flex align-start ga-4">
                <v-avatar color="rgba(238, 179, 54, 0.12)" size="40">
                  <v-icon color="highlight" size="20">mdi-map-marker</v-icon>
                </v-avatar>
                <div>
                  <h3 class="text-h6 text-accent">Contact</h3>
                  <p class="text-body-1">
                    Westerkerk Eemdijk<br />
                    Eemdijk 63<br />
                    3754 ND Eemdijk
                  </p>
                  <p class="text-body-1">033-2988572</p>
                  <v-btn
                    color="highlight"
                    variant="text"
                    prepend-icon="mdi-arrow-right"
                    to="/contact"
                    class="btn-link"
                  >
                    Naar contactpagina
                  </v-btn>
                </div>
              </v-col>
            </v-row>

            <div
              class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center pt-5 ga-4"
            >
              <div>
                <p class="text-kicker text-highlight">Meer achtergrond</p>
                <p class="text-body-1 mb-0">
                  Lees meer over wat wij geloven, onze gereformeerde traditie en de geschiedenis van
                  onze gemeente.
                </p>
              </div>
              <v-btn
                color="accent"
                variant="text"
                prepend-icon="mdi-arrow-right"
                to="/over-ons/achtergrond"
                class="btn-link align-self-start"
                style="flex-shrink: 0"
              >
                Meer over onze gemeente
              </v-btn>
            </div>
          </section>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useDisplay } from 'vuetify';
import { getSundayServiceText } from '../../shared/serviceTimes';

const { smAndDown } = useDisplay();
const activeSlide = ref(0);
const sundayEveningServiceDescription = getSundayServiceText('seasonalDescription');

const heroSlides = [
  {
    src: '/images/kerkBuiten.jpeg',
    alt: 'Buitenzijde van de Westerkerk in Eemdijk'
  },
  {
    src: '/images/polderEemdijk.jpeg',
    alt: 'Landschap rond Eemdijk'
  },
  {
    src: '/images/kerkBinnen.jpeg',
    alt: 'Binnenzijde van de kerkzaal'
  }
];

function setSlide(index: number) {
  activeSlide.value = index;
}
</script>

<style scoped>
.hero-shell {
  position: relative;
  margin-bottom: 1.5rem;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
}

.about-hero {
  border-radius: 0;
  overflow: hidden;
}

.hero-scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(19, 28, 20, 0.3), rgba(19, 28, 20, 0.05));
}

.hero-overlay {
  position: absolute;
  left: max(1rem, calc(50vw - 410px));
  bottom: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 560px;
  pointer-events: none;
}

.hero-title {
  color: #ffffff;
  line-height: 0.95;
  text-wrap: balance;
}

.hero-copy {
  max-width: 36rem;
  color: rgba(255, 255, 255, 0.92);
  font-size: 1.05rem;
  line-height: 1.6;
}

.hero-top-dots {
  position: absolute;
  top: 1rem;
  left: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transform: translateX(-50%);
  pointer-events: auto;
}

.hero-dot {
  width: 0.72rem;
  height: 0.72rem;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.45);
  box-shadow: 0 0 0 1px rgba(19, 28, 20, 0.16);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.hero-dot-active {
  background: #ffffff;
  transform: scale(1.2);
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  pointer-events: auto;
}

@media (max-width: 960px) {
  .about-hero {
    border-radius: 0;
  }

  .hero-overlay {
    left: 1rem;
    bottom: 1rem;
    max-width: 440px;
  }

  .hero-title {
    font-size: 3.25rem !important;
  }
}

@media (max-width: 600px) {
  .about-hero {
    height: 460px !important;
  }

  .hero-overlay {
    left: 0.75rem;
    right: 0.75rem;
    bottom: 0.75rem;
    align-items: stretch;
    max-width: none;
  }

  .hero-title {
    font-size: 2.75rem !important;
  }

  .hero-copy {
    font-size: 1rem;
  }

  .hero-top-dots {
    top: 0.85rem;
  }

  .hero-actions {
    flex-direction: column;
  }
}
</style>
